import React, { useEffect, useState } from 'react';
import Icon from '../../../Icon/Icon';
import { useTranslation } from 'react-i18next';
import InputZoneSearch from './InputZoneSearch/InputZoneSearch';
import { newCampaignActions } from '../../../../store';
import SwitcherButton from '../../../Buttons/CommonSwitcher';
import { BlockPicker } from 'react-color';
import { getRandomHex, hexaToRgb, rgbToHex } from '../../../../utils/functions';
import PoisPdf from '../../../Pdf/PoisPdf';
import { usePDF } from '@react-pdf/renderer';
import { CSVLink } from 'react-csv';
import ModalPdf from '../../../Modal/ModalPdf/ModalPdf';
import { useAuth } from '../../../../containers/UseAuth';

const randomRgb = getRandomHex(15);
const MIN_POI_RADIUS_METERS = 100;
const MAX_POI_RADIUS_METERS = 5000;
const POI_RADIUS_STEP_METERS = 100;
const POI_RADIUS_OPTIONS = [200, 400, 600, 800, 1000, 1500, 2000];

const Zone = ({
    data,
    index,
    onRemove,
    toggleActive,
    onChangeRadius,
    dispatch,
    newCampaign,
}) => {
    const [showPicker, setShowPicker] = useState('hidden');
    let selectedColor = data.results;

    const locationZones =
        (newCampaign?.location?.zones ?? 0) !== 0
            ? newCampaign.location.zones
            : [];
    const mapLocationZones =
        (newCampaign?.mapLocations?.zones?.length ?? 0) !== 0
            ? newCampaign.mapLocations.zones
            : [];

    const handleColor = (color) => {
        color = hexaToRgb(color);

        const arr = [];
        selectedColor.forEach((result) => {
            arr.push({ ...result, color });
        });
        selectedColor = arr;

        const newZonesLocation = locationZones.map((z) => {
            if (z.search === data.search) {
                return {
                    ...z,
                    results: arr,
                };
            } else {
                return z;
            }
        });

        const newLocation = Object.assign({}, newCampaign.location);
        newLocation.zones = newZonesLocation;
        dispatch(newCampaignActions.setLocation(newLocation));

        if (mapLocationZones.length > 0) {
            const newLocationZones = mapLocationZones.map((z) => {
                if (z.search === data.search) {
                    return {
                        ...z,
                        results: arr,
                    };
                } else {
                    return z;
                }
            });

            const newLocations = Object.assign({}, newCampaign.location);
            newLocations.zones = newLocationZones;
            // dispatch(newCampaignActions.setLocation(newLocations));
            dispatch(newCampaignActions.setMapLocations(newLocations));
        }
    };

    const handleFilter = () => {
        toggleActive(data);
    };
    const currentRadius =
        Number(data?.results?.[0]?.radius) > 0
            ? Number(data.results[0].radius)
            : 400;
    const handleRadiusDelta = (delta) => {
        const nextRadius = Math.min(
            MAX_POI_RADIUS_METERS,
            Math.max(
                MIN_POI_RADIUS_METERS,
                currentRadius + delta * POI_RADIUS_STEP_METERS
            )
        );
        onChangeRadius(data, nextRadius);
    };

    const handlePicker = () => {
        let isHidden = showPicker === 'hidden';

        if (!isHidden) {
            setShowPicker('hidden');
        } else {
            setShowPicker('');
        }
    };

    const openLocationOnMap = (zone) => {
        const newLocations = JSON.parse(JSON.stringify(newCampaign.location));

        const index = newLocations.zones.findIndex(
            (z) => JSON.stringify(z) === JSON.stringify(zone)
        );

        const removedZone = newLocations.zones.splice(index, 1)[0];

        if (removedZone !== undefined) {
            newLocations.zones.push(removedZone);
        }

        dispatch(newCampaignActions.setMapLocations(newLocations));
    };

    return (
        <li className="py-3 flex">
            <div className="flex justify-between w-full p-1">
                <span
                    style={{ backgroundColor: selectedColor[0].color }}
                    className={`h-5 w-5 cursor-pointer rounded-full`}
                    onClick={() => handlePicker()}>
                    <BlockPicker
                        styles={{ zIndex: '3000' }}
                        className={`absolute mt-8 ${showPicker} transition-opacity`}
                        color={selectedColor[0].color}
                        colors={randomRgb}
                        onChangeComplete={(e) => {
                            handleColor(e.hex);
                        }}
                    />
                </span>
                <label className="ml-2 inline w-full text-gray-500">
                    <span
                        onClick={() => openLocationOnMap(data)}
                        htmlFor={`placement-${index}`}>
                        {data.search}
                    </span>
                </label>
                <div className="flex items-center gap-2 mr-4">
                    <button
                        type="button"
                        className="px-2 py-0.5 border rounded text-gray-500"
                        onClick={() => handleRadiusDelta(-1)}>
                        -
                    </button>
                    <span className="text-xs text-gray-500 w-14 text-center">
                        {Math.round(currentRadius)} m
                    </span>
                    <button
                        type="button"
                        className="px-2 py-0.5 border rounded text-gray-500"
                        onClick={() => handleRadiusDelta(1)}>
                        +
                    </button>
                </div>
                <SwitcherButton
                    className={'text-gray-500 mr-4'}
                    labelOn={'Filtering on'}
                    labelOff={'Filtering off'}
                    status={data.active}
                    onToggle={() => {
                        handleFilter();
                    }}
                    duration={50}
                />
                <Icon
                    onClick={() => onRemove(data)}
                    name="minus-circle"
                    className="text-gray-500 cursor-pointer mr-4 h-5"
                />
            </div>
        </li>
    );
};

const NearPlace = ({
    zones,
    dispatch,
    newCampaign,
    location,
    filteredScreensLocal,
    filterScreenByPoi = false,
    setFilterScreenByPoi = () => {},
}) => {
    const { t } = useTranslation();
    const selectedScreens = newCampaign.selectedScreens;
    const isResizeMapOn = newCampaign.isResizeMapOn;
    const [isLoadingPDF, setIsLoadingPDF] = useState(false);
    const [showPdf, setShowPdf] = useState(false);
    const [newPoiRadius, setNewPoiRadius] = useState(400);
    const auth = useAuth();

    const removeZone = (zone) => {
        const newLocation = Object.assign({}, newCampaign.location);
        newLocation.zones = zones.filter((z) => z !== zone);
        dispatch(newCampaignActions.setLocation(newLocation));
        dispatch(newCampaignActions.setMapLocations(newLocation));
    };

    const toggleActive = (zone) => {
        const newZones = zones.map((z) => {
            if (z.search === zone.search) {
                const active = !zone.active;
                return { ...z, active };
            } else {
                return z;
            }
        });

        const newLocation = Object.assign({}, newCampaign.location);
        newLocation.zones = newZones;

        dispatch(newCampaignActions.setLocation(newLocation));
        dispatch(newCampaignActions.setMapLocations(newLocation));
    };
    const updateZoneRadius = (zone, radius) => {
        const newZones = zones.map((z) => {
            if (z.search !== zone.search) return z;
            const results = Array.isArray(z.results)
                ? z.results.map((result) => ({ ...result, radius }))
                : [];
            return { ...z, results };
        });

        const newLocation = Object.assign({}, newCampaign.location);
        newLocation.zones = newZones;
        dispatch(newCampaignActions.setLocation(newLocation));
        dispatch(newCampaignActions.setMapLocations(newLocation));
    };

    const [url, setUrl] = useState([]);
    const [colors, setColors] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const pdf = (
        <PoisPdf
            colors={colors}
            markers={url.join('&')}
            screens={
                auth.isAdmin()
                    ? filteredScreensLocal
                    : filteredScreensLocal.filter(
                          (screen) =>
                              !screen.label.toLowerCase().includes('test')
                      )
            }
            selectedScreens={selectedScreens}
        />
    );
    const [instance, setInstance] = usePDF({ document: pdf });

    useEffect(() => {
        let newURL = [];
        let newColors = [];
        let newCSVData = [];
        zones.forEach((zone) => {
            zone.results.forEach((result) => {
                newCSVData.push({
                    name: zone.search,
                    lat: result.lat,
                    lng: result.lng,
                });

                const color = result.color;
                let hexa;
                if (color) {
                    const arr = color?.split('(');
                    const splited = arr[1]?.split(')');
                    const rgb = splited[0]?.split(',');
                    hexa = rgbToHex(rgb[0], rgb[1], rgb[2]);
                }
                const urlPath = `markers=size:tiny%7Ccolor:0x${hexa}%7C${result.lat},${result.lng}`;
                newURL.push(urlPath);
            });

            newColors.push({
                label: zone.search,
                color: zone.results[0].color,
            });
        });
        setUrl(newURL);
        setCsvData(newCSVData);
        setColors(newColors);
    }, [zones]);

    const downloadPDf = (e) => {
        setIsLoadingPDF(true);
        const pdfpois = (
            <PoisPdf
                colors={colors}
                markers={url.join('&')}
                screens={filteredScreensLocal}
                selectedScreens={selectedScreens}
            />
        );
        setInstance({ document: pdfpois });
    };

    if (!instance.loading && instance.url && isLoadingPDF) {
        setIsLoadingPDF(false);
        setShowPdf(true);
    }

    if (instance.error) {
        console.log('instance.error: ', instance.error);
    }

    return (
        <div className="module rounded m-1 mb-4" id="screenshot">
            <div className="header flex justify-between content-center">
                <div className="title text-xl font-extralight w-full">
                    {t(
                        'sections.campaigns.new-campaign.wizard-basics.location.nearPlace'
                    )}
                </div>
                <div className="pr-2">
                    <SwitcherButton
                        labelOn={'POI filtering on'}
                        labelOff={'POI filtering off'}
                        status={filterScreenByPoi}
                        onToggle={() => setFilterScreenByPoi((prev) => !prev)}
                        duration={100}
                    />
                </div>
            </div>
            <div
                className={`body flex flex-col md:flex-row justify-between rounded-sm p-1 w-full ${
                    isResizeMapOn ? 'hidden' : ''
                }`}>
                <div className="w-full md:w-3/6 mb-4">
                    <label
                        className="text-sm font-medium text-gray-700"
                        htmlFor="zone-search">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.location.place-label'
                        )}
                    </label>
                    <div className="mt-4">
                        <InputZoneSearch
                            id="zone-search"
                            radius={newPoiRadius}
                            locationForFilters={location}
                        />
                    </div>
                    <div className="mt-2">
                        <label
                            className="text-xs font-medium text-gray-500"
                            htmlFor="poi-radius-default">
                            Rango por defecto de nuevos POIs
                        </label>
                        <select
                            id="poi-radius-default"
                            className="block mt-1 border rounded p-1 text-xs"
                            value={newPoiRadius}
                            onChange={(e) =>
                                setNewPoiRadius(Number(e.target.value) || 400)
                            }>
                            {POI_RADIUS_OPTIONS.map((value) => (
                                <option key={value} value={value}>
                                    {value} m
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            {zones.length > 0 && (
                <div className={'flex justify-between'}>
                    <span className="block text-sm font-medium text-gray-700">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.location.history-label'
                        )}
                    </span>
                    <div className="cursor-pointer">
                        <CSVLink
                            filename={`pois.csv`}
                            data={csvData}
                            target={'_blank'}>
                            <Icon
                                name="file-csv"
                                className="text-blue-300"
                            />
                            <span className="text-blue-300 text-sm pl-2 pr-4">
                                {t(
                                    'common.components.ReportsTable.export-to-csv'
                                )}
                            </span>
                        </CSVLink>
                        <span
                            className="text-gray-500 text-sm"
                            onClick={downloadPDf}>
                            <Icon
                                name="file-pdf"
                                className="text-sm font-medium text-gray-500 mr-2"
                            />
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.location.button-placeholder'
                            )}
                        </span>
                    </div>
                </div>
            )}
            <div className="block">
                <ul className="list-none divide-y py-2">
                    {zones.map((zone, index) => (
                        <Zone
                            data={zone}
                            dispatch={dispatch}
                            newCampaign={newCampaign}
                            toggleActive={toggleActive}
                            onChangeRadius={updateZoneRadius}
                            onRemove={removeZone}
                            key={index}
                            index={index}
                        />
                    ))}
                </ul>
            </div>
            {showPdf && (
                <ModalPdf
                    isShowPdf={showPdf}
                    pdfUrl={instance.url}
                    titleIframe={`#${newCampaign.id}-maps.pdf`}
                    setIsShowPdf={() => setShowPdf(false)}
                    classNameStyles="h-full pb-6"
                />
            )}
        </div>
    );
};

export default NearPlace;
